import { EnvironmentConfig, NavigationItem, MembershipCategory, MembershipBenefit, CodeOfEthics } from '../types';

// Environment Configuration
export const ENV_CONFIG: EnvironmentConfig = {
  comingSoon: process.env.REACT_APP_COMING_SOON === 'true',
  maintenanceMode: process.env.REACT_APP_MAINTENANCE_MODE === 'true',
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api',
  environment: (process.env.REACT_APP_ENVIRONMENT as 'development' | 'staging' | 'production') || 'development',
  siteName: process.env.REACT_APP_SITE_NAME || 'CSEAN',
  siteTagline: process.env.REACT_APP_SITE_TAGLINE || 'Cyber Security Experts Association of Nigeria',
  excludedPages: (process.env.REACT_APP_EXCLUDED_PAGES || '').split(',').filter(Boolean),
};

// Navigation Configuration
export const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: 'home',
    label: 'Home',
    path: '/',
  },
  {
    id: 'about',
    label: 'About Us',
    path: '/about',
    children: [
      {
        id: 'about-executives',
        label: 'Executive Leadership',
        path: '/about/executives',
      },
    ],
  },
  {
    id: 'membership',
    label: 'Membership',
    path: '#',
    children: [
      {
        id: 'membership-categories',
        label: 'Categories',
        path: '/membership/categories',
      },
      {
        id: 'membership-benefits',
        label: 'Benefits',
        path: '/membership/benefits',
      },
      {
        id: 'membership-code-of-ethics',
        label: 'Code of Ethics',
        path: '/membership/code-of-ethics',
      },
      {
        id: 'membership-application',
        label: 'Application',
        path: '/membership/application',
      },
      {
        id: 'membership-application-status',
        label: 'Check Status',
        path: '/membership/application-status',
      },
    ],
  },
  {
    id: 'contact',
    label: 'Contact',
    path: '/contact',
  },
];

// Membership Categories
export const MEMBERSHIP_CATEGORIES: MembershipCategory[] = [
  {
    id: 'student',
    name: 'Student Member',
    description: 'For undergraduates pursuing Computer Science or a related discipline',
    price: 25000,
    currency: 'NGN',
    renewalPrice: 5000,
    features: [
      'Access to CSEAN online tools and technical white papers',
      'Support for projects, research and development',
      'Networking opportunities with IT professionals',
      'Mentorship programs',
      'Discounted training programs',
    ],
  },
  {
    id: 'affiliate',
    name: 'Affiliate Member',
    description: 'For non-computing graduates with at least five years of post-NYSC experience',
    price: 45000,
    currency: 'NGN',
    renewalPrice: 10000,
    features: [
      'Basic IT tools and knowledge resources',
      'Decision-making support for IT-related matters',
      'Professional networking opportunities',
      'Access to industry insights',
      'Training materials for non-IT professionals',
    ],
  },
  {
    id: 'graduate',
    name: 'Graduate Member',
    description: 'For recent computing graduates with up to one year of post-NYSC experience',
    price: 45000,
    currency: 'NGN',
    renewalPrice: 10000,
    features: [
      'Access to career development resources',
      'Professional training materials',
      'Networking with experienced professionals',
      'Career guidance and mentorship',
      'Pathway to Associate membership',
    ],
  },
  {
    id: 'associate',
    name: 'Associate Member',
    description: 'For computing graduates with at least three years of post-NYSC experience',
    price: 50000,
    currency: 'NGN',
    renewalPrice: 10000,
    features: [
      'Access to career development resources',
      'Professional training materials',
      'Networking with experienced professionals',
      'Career guidance and mentorship',
      'Pathway to Professional membership',
    ],
  },
  {
    id: 'professional',
    name: 'Professional Member',
    description: 'For computing graduates with at least five years of post-NYSC experience',
    price: 60000,
    currency: 'NGN',
    renewalPrice: 10000,
    features: [
      'Full access to all professional resources',
      'Advanced training and certification support',
      'Conference and event discounts',
      'Industry networking and collaboration',
      'Technical reports and research access',
      'Leadership opportunities',
    ],
    isPopular: true,
  },
];

// Membership Benefits
export const MEMBERSHIP_BENEFITS: MembershipBenefit[] = [
  {
    id: 'networking',
    title: 'Professional Networking',
    description: 'Connect with cybersecurity experts and industry leaders',
    icon: 'users',
    category: 'general',
  },
  {
    id: 'training',
    title: 'Training & Development',
    description: 'Access to exclusive training programs and workshops',
    icon: 'academic-cap',
    category: 'professional',
  },
  {
    id: 'certifications',
    title: 'Certification Support',
    description: 'Guidance and support for cybersecurity certifications',
    icon: 'badge-check',
    category: 'professional',
  },
  {
    id: 'resources',
    title: 'Technical Resources',
    description: 'Access to latest research, reports, and best practices',
    icon: 'document-text',
    category: 'general',
  },
  {
    id: 'conferences',
    title: 'Conference Access',
    description: 'Discounted or free access to CSEAN conferences and events',
    icon: 'calendar',
    category: 'premium',
  },
  {
    id: 'mentorship',
    title: 'Mentorship Programs',
    description: 'Guidance from experienced cybersecurity professionals',
    icon: 'user-group',
    category: 'premium',
  },
];

// Code of Ethics - Official CSEAN Principles
export const CODE_OF_ETHICS: CodeOfEthics[] = [
  {
    id: 'professional-excellence',
    title: 'Professional Excellence',
    description: 'Discharge all professional activities with honesty, competency and diligence',
    principles: [
      'Act responsibly and legally at all times',
      'Maintain high ethical standards in all professional activities',
      'Demonstrate competency and diligence in work performance',
      'Uphold honesty in all professional interactions',
    ],
  },
  {
    id: 'confidentiality',
    title: 'Confidentiality',
    description: 'Maintain confidentiality of proprietary or sensitive information',
    principles: [
      'Protect proprietary information encountered during professional duties',
      'Safeguard sensitive information with appropriate security measures',
      'Respect confidentiality agreements and non-disclosure requirements',
      'Report security breaches or confidentiality violations promptly',
    ],
  },
  {
    id: 'reputation-protection',
    title: 'Reputation Protection',
    description: 'Refrain from activities that damage reputation or are detrimental to stakeholders',
    principles: [
      'Protect the reputation of employers and clients',
      'Avoid actions detrimental to the information security profession',
      'Maintain the integrity and reputation of CSEAN',
      'Act in ways that benefit and protect the Nation',
    ],
  },
  {
    id: 'continuing-education',
    title: 'Continuing Education',
    description: 'Meet continuing education standards and assist others in their development',
    principles: [
      'Maintain current knowledge in information security practices',
      'Meet professional development requirements',
      'Assist others in improving their knowledge and abilities',
      'Share expertise to strengthen the professional community',
    ],
  },
  {
    id: 'professional-advancement',
    title: 'Professional Advancement',
    description: 'Contribute to the advancement of information security practices in Nigeria',
    principles: [
      'Support the transformation of Information Security practices landscape',
      'Promote cultural and professional advancement in cybersecurity',
      'Contribute to national cybersecurity initiatives',
      'Foster excellence in information security across Nigeria',
    ],
  },
];

// API Endpoints
export const API_ENDPOINTS = {
  MEMBERSHIP: {
    APPLICATION: '/membership/application',
    CATEGORIES: '/membership/categories',
    BENEFITS: '/membership/benefits',
  },
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REGISTER: '/auth/register',
  },
} as const;
